/**
 * m3 echo Frontend - Optimization Radar Chart Component
 *
 * Displays a radar/spider chart showing scores for optimization criteria.
 * Supports both V1 (6 criteria) and V2 (7 AI-Visibility categories).
 */

import React from 'react';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import { Box, Card, CardContent, Tooltip as MuiTooltip, Typography, useTheme } from '@mui/material';
import { OptimizationTest, AnalysisResultV2, CategoryResultV2 } from '../types/api';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { isV2Test, CATEGORY_ORDER, getScoreColor, V2CategoryKey } from '../utils/scoringUtils';

const RADAR_LABELS: Record<V2CategoryKey, string> = {
  entity: 'Entity-Klarheit',
  content: 'Zitierfähigkeit',
  reputation: 'Reputation',
  usp: 'USP',
  local: 'Lokale Relevanz',
  thirdparty: 'Drittquellen',
  technical: 'Techn. Signale',
};

interface OptimizationRadarChartProps {
  test: OptimizationTest;
  previousTest?: OptimizationTest;
  showComparison?: boolean;
}

const OptimizationRadarChart: React.FC<OptimizationRadarChartProps> = ({
  test,
  previousTest,
  showComparison = false,
}) => {
  const theme = useTheme();
  const reduceMotion = useReducedMotion();

  const prepareChartData = () => {
    if (isV2Test(test)) {
      // V2: 7 AI-Visibility categories from analysis_result JSON
      const result = test.analysis_result as AnalysisResultV2 | null;
      const prevResult = previousTest?.analysis_result as AnalysisResultV2 | null;

      return CATEGORY_ORDER.map((key) => {
        const catData = result?.[key] as CategoryResultV2 | undefined;
        const prevCatData = prevResult?.[key] as CategoryResultV2 | undefined;

        const dataPoint: Record<string, unknown> = {
          criterion: RADAR_LABELS[key],
          current: catData?.score ?? 0,
          fullMark: 100,
        };

        if (showComparison && prevCatData) {
          dataPoint.previous = prevCatData.score ?? 0;
        }

        return dataPoint;
      });
    }

    // V1 fallback: 6 legacy criteria from individual score columns
    const criteriaLabels: Record<string, string> = {
      renewable_energy_score: 'Ökostrom',
      customer_satisfaction_score: 'Kundenzufr.',
      pricing_transparency_score: 'Preistransp.',
      local_partnerships_score: 'Partnerschaften',
      web_visibility_score: 'Web-Sichtb.',
      sustainability_communication_score: 'Nachhaltigkeit',
    };

    return Object.entries(criteriaLabels).map(([key, label]) => {
      const currentValue = test[key as keyof typeof test] as number | null;
      const previousValue = previousTest
        ? (previousTest[key as keyof typeof previousTest] as number | null)
        : null;

      const dataPoint: Record<string, unknown> = {
        criterion: label,
        current: currentValue || 0,
        fullMark: 100,
      };

      if (showComparison && previousValue !== null) {
        dataPoint.previous = previousValue;
      }

      return dataPoint;
    });
  };

  const chartData = prepareChartData();

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const score = payload[0]?.value ?? 0;
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            {payload[0].payload.criterion}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.name === 'current' ? getScoreColor(score) : entry.color }}
            >
              {entry.name === 'current' ? 'Aktuell' : 'Vorher'}: {entry.value}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  if (test.status !== 'completed' || test.overall_score === null) {
    return (
      <Card>
        <CardContent>
          <MuiTooltip title="Radar-Diagramm zur Bewertung Ihrer AI-Sichtbarkeit in verschiedenen Kategorien. Je weiter die Fläche nach außen reicht, desto besser ist die Bewertung in dieser Kategorie." arrow>
            <Typography variant="h6" gutterBottom sx={{ cursor: 'help' }}>
              Ergebnisse der Analyse
            </Typography>
          </MuiTooltip>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={400}
          >
            <Typography color="text.secondary">
              Test noch nicht abgeschlossen
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <MuiTooltip title="Radar-Diagramm zur Bewertung Ihrer AI-Sichtbarkeit in verschiedenen Kategorien. Je weiter die Fläche nach außen reicht, desto besser ist die Bewertung in dieser Kategorie." arrow>
          <Typography variant="h6" gutterBottom sx={{ cursor: 'help' }}>
            Ergebnisse der Analyse
          </Typography>
        </MuiTooltip>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {isV2Test(test)
            ? 'Bewertung der AI-Visibility nach 7 Kategorien'
            : 'Bewertung der Web-Präsenz nach LLM-Optimierungs-Kriterien'}
        </Typography>

        <ResponsiveContainer width="100%" height={350}>
          <RadarChart data={chartData}>
            <PolarGrid stroke={theme.palette.divider} />
            <PolarAngleAxis
              dataKey="criterion"
              tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 100]}
              tick={false}
              axisLine={false}
            />

            <Radar
              name="Aktuell"
              dataKey="current"
              stroke={theme.palette.primary.main}
              fill={theme.palette.primary.main}
              fillOpacity={0.6}
              isAnimationActive={!reduceMotion}
              animationDuration={800}
              animationEasing="ease-out"
            />

            {showComparison && previousTest && (
              <Radar
                name="Vorher"
                dataKey="previous"
                stroke={theme.palette.secondary.main}
                fill={theme.palette.secondary.main}
                fillOpacity={0.3}
                isAnimationActive={!reduceMotion}
                animationDuration={800}
                animationEasing="ease-out"
                animationBegin={100}
              />
            )}

            <Tooltip content={<CustomTooltip />} />
            {showComparison && previousTest && <Legend />}
          </RadarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default OptimizationRadarChart;
